import { useEffect, useRef } from 'react';
import { useAtomValue } from 'jotai';

import { activeAtom } from '@/atoms';
import { smoothScrollTo } from '@/utils';

/**
 * Hook to handle smooth scrolling to sections based on active state
 * Optimized for performance and cross-platform compatibility
 */
export const useScrollToLocation = () => {
  const active = useAtomValue(activeAtom);
  const isScrollingRef = useRef(false);
  const scrollTimeoutRef = useRef(null);

  useEffect(() => {
    if (!active) return;

    // Allow new navigation actions to interrupt an ongoing scroll
    if (isScrollingRef.current) {
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
      isScrollingRef.current = false;
    }

    const element = document.getElementById(active);
    if (!element) return;

    // Set scrolling lock to prevent interference
    isScrollingRef.current = true;

    // Clear any existing timeout
    if (scrollTimeoutRef.current) {
      clearTimeout(scrollTimeoutRef.current);
    }

    // Use our utility function for consistent scrolling
    if (active === 'Contacts') {
      element.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
      });
    } else {
      smoothScrollTo(active, -40);
    }

    // Release the scrolling lock after animation completes
    scrollTimeoutRef.current = setTimeout(() => {
      isScrollingRef.current = false;
    }, 500);

    // Cleanup timeout on unmount or when active changes
    return () => {
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
    };
  }, [active]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
    };
  }, []);
};
